import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getNotifications, markNotificationRead, markAllNotificationsRead } from '../api';
import { useAuth } from '../AuthContext';
import { API_URL } from '../apiConfig';

const NotificationContext = createContext();

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!currentUser) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    // Role-based for staff, ID-based for customers
    // Normalize: handle ObjectId objects that may have been stored in old sessions
    const rawId = currentUser.id?.$oid || currentUser.id?.toString?.() || currentUser.id || currentUser._id;
    const identifier = (currentUser.role && currentUser.role !== 'user' && currentUser.role !== 'customer') 
      ? currentUser.role 
      : rawId;

    if (!identifier) return;

    try {
      setLoading(true);
      const data = await getNotifications(identifier);
      setNotifications(data);
      const unread = data.filter(n => !n.isRead).length;
      setUnreadCount(unread);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchNotifications();
    // Poll every 60 seconds
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAsRead = async (id) => {
    try {
      await markNotificationRead(id);
      setNotifications(prev => 
        prev.map(n => n._id === id ? { ...n, isRead: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const markAllRead = async () => {
    const rawId = currentUser.id?.$oid || currentUser.id?.toString?.() || currentUser.id || currentUser._id;
    const identifier = (currentUser.role && currentUser.role !== 'user' && currentUser.role !== 'customer') 
      ? currentUser.role 
      : rawId;
      
    try {
      await markAllNotificationsRead(identifier);
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const clearAll = async () => {
    const rawId = currentUser.id?.$oid || currentUser.id?.toString?.() || currentUser.id || currentUser._id;
    const identifier = (currentUser.role && currentUser.role !== 'user' && currentUser.role !== 'customer') 
      ? currentUser.role 
      : rawId;
      
    try {
      setLoading(true);
      await fetch(`${API_URL}/notifications/clear-all/${identifier}`, {
        method: 'DELETE'
      });
      setNotifications([]);
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to clear all notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <NotificationContext.Provider value={{ 
      notifications, 
      unreadCount, 
      loading, 
      fetchNotifications, 
      markAsRead, 
      markAllRead,
      clearAll
    }}>
      {children}
    </NotificationContext.Provider>
  );
};
